import { DOCUMENTATION_ROOT } from '../utils';

import {
  ApiOperation,
  ApiParameter,
  ApiParameterValues,
  ApiResponse,
  ApiRoute,
  ApiSchema,
  ApiSchemaField,
  ApiService,
} from './types';

/** Docs path of the API reference page (docs/NN-api-reference). */
export const API_REFERENCE_PATH = 'api-reference';

/**
 * Endpoints tagged with this in their service are public: they appear in the
 * API reference. Tagging an endpoint is all it takes to document it.
 */
export const PUBLIC_API_TAG = 'PRODUCTION_READY';

/** Services listed in the API reference, in nav order. */
export const API_SERVICES: ApiService[] = [
  { id: 'cloud-account', title: 'Cloud Accounts' },
  { id: 'policies', title: 'Environments' },
  { id: 'notification', title: 'Findings' },
  { id: 'inventory', title: 'Inventory' },
  { id: 'abac', title: 'Data Access Management' },
];

/** Try it only sends these; other methods are documented, not executable. */
export const TRY_IT_METHODS = ['get'];

const HTTP_METHODS = ['get', 'post', 'put', 'patch', 'delete'];
const MAX_SCHEMA_DEPTH = 6;
const MAX_FIELD_ROWS = 400;

export const apiSpecUrl = (service: string): string =>
  `/v1/${service}/openapi.json`;

export const apiServiceUrl = (service: string): string =>
  `${DOCUMENTATION_ROOT}/${API_REFERENCE_PATH}/${service}`;

export const apiOperationUrl = (operation: ApiOperation): string =>
  `${apiServiceUrl(operation.service)}/${operation.id}`;

/** Parses an API reference URL; undefined for every other docs page. */
export const parseApiRoute = (pathname: string): ApiRoute | undefined => {
  const prefix = `${DOCUMENTATION_ROOT}/${API_REFERENCE_PATH}/`;
  if (!pathname.startsWith(prefix)) return undefined;
  const [service, operation] = pathname
    .slice(prefix.length)
    .split('/')
    .filter(Boolean)
    .map(decodeURIComponent);
  return service ? { service, operation } : undefined;
};

const slug = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/** Follows a local "#/components/..." reference. */
const lookupRef = (spec: any, ref: string): any =>
  ref.startsWith('#/')
    ? ref
        .slice(2)
        .split('/')
        .reduce(
          (node, key) => node?.[key.replace(/~1/g, '/').replace(/~0/g, '~')],
          spec,
        )
    : undefined;

/**
 * Inlines $refs so the UI can walk a schema without the spec. Each branch
 * tracks the refs above it, so recursive models stop instead of looping.
 */
export const resolveSchema = (
  spec: any,
  schema: any,
  parents: string[] = [],
): ApiSchema => {
  if (!schema || typeof schema !== 'object') return {};
  if (schema.$ref) {
    if (parents.includes(schema.$ref) || parents.length > 32)
      return { title: schema.$ref.split('/').pop(), recursive: true };
    return resolveSchema(spec, lookupRef(spec, schema.$ref), [
      ...parents,
      schema.$ref,
    ]);
  }
  const resolved: ApiSchema = { ...schema };
  if (schema.properties)
    resolved.properties = Object.fromEntries(
      Object.entries(schema.properties).map(([key, value]) => [
        key,
        resolveSchema(spec, value, parents),
      ]),
    );
  if (schema.items) resolved.items = resolveSchema(spec, schema.items, parents);
  if (schema.additionalProperties && schema.additionalProperties !== true)
    resolved.additionalProperties = resolveSchema(
      spec,
      schema.additionalProperties,
      parents,
    );
  for (const key of ['allOf', 'anyOf', 'oneOf'] as const)
    if (Array.isArray(schema[key]))
      resolved[key] = schema[key].map(item =>
        resolveSchema(spec, item, parents),
      );
  return resolved;
};

const isNullSchema = (schema: ApiSchema) =>
  schema.type === 'null' ||
  (Array.isArray(schema.type) &&
    schema.type.length === 1 &&
    schema.type[0] === 'null');

/**
 * Collapses the wrappers generators emit around one real schema: a single
 * allOf, and the "anyOf: [X, null]" that OpenAPI 3.1 uses for optional X.
 */
export const unwrapSchema = (schema: ApiSchema): ApiSchema => {
  if (schema.allOf?.length === 1 && !schema.properties)
    return unwrapSchema({
      ...schema.allOf[0],
      description: schema.description ?? schema.allOf[0].description,
      allOf: undefined,
    });
  if (schema.allOf && schema.allOf.length > 1)
    return {
      ...schema,
      allOf: undefined,
      type: 'object',
      properties: Object.assign(
        {},
        ...schema.allOf.map(part => unwrapSchema(part).properties ?? {}),
        schema.properties,
      ),
      required: [
        ...(schema.required ?? []),
        ...schema.allOf.flatMap(part => unwrapSchema(part).required ?? []),
      ],
    };
  for (const key of ['anyOf', 'oneOf'] as const) {
    const options = schema[key]?.filter(option => !isNullSchema(option));
    if (options?.length === 1)
      return unwrapSchema({
        ...options[0],
        description: schema.description ?? options[0].description,
        nullable: true,
      });
  }
  return schema;
};

/** A short type label such as "string (date-time)" or "array of Alert". */
export const schemaTypeLabel = (input: ApiSchema): string => {
  const schema = unwrapSchema(input);
  if (schema.recursive) return schema.title ?? 'object';
  const variants = schema.anyOf ?? schema.oneOf;
  if (variants)
    return variants
      .filter(option => !isNullSchema(option))
      .map(schemaTypeLabel)
      .join(' | ');
  const types = (Array.isArray(schema.type) ? schema.type : [schema.type])
    .filter(type => type && type !== 'null')
    .join(' | ');
  if (types === 'array' && schema.items)
    return `array of ${schemaTypeLabel(schema.items)}`;
  // Named models read better than "object", e.g. "array of Alert".
  if ((!types || types === 'object') && schema.properties)
    return schema.title ?? 'object';
  const label = types || (schema.enum ? 'string' : 'any');
  return schema.format ? `${label} (${schema.format})` : label;
};

/**
 * Flattens a schema into indented rows for a field table. Arrays of objects
 * list their item fields one level deeper; maps list their value fields.
 */
export const schemaFields = (input?: ApiSchema): ApiSchemaField[] => {
  const rows: ApiSchemaField[] = [];
  const walk = (schema: ApiSchema, depth: number) => {
    let node = unwrapSchema(schema);
    while (node.items) node = unwrapSchema(node.items);
    if (
      node.additionalProperties &&
      typeof node.additionalProperties === 'object' &&
      !node.properties
    )
      node = unwrapSchema(node.additionalProperties);
    if (!node.properties || depth > MAX_SCHEMA_DEPTH) return;
    for (const [name, child] of Object.entries(node.properties)) {
      if (rows.length >= MAX_FIELD_ROWS) return;
      const field = unwrapSchema(child);
      rows.push({
        name,
        depth,
        type: schemaTypeLabel(child),
        required: !!node.required?.includes(name),
        description: field.description ?? field.title,
        values: field.enum?.map(String),
      });
      if (!field.recursive) walk(child, depth + 1);
    }
  };
  if (input) walk(input, 0);
  return rows;
};

const firstExample = (schema: ApiSchema): unknown =>
  schema.example ??
  (Array.isArray(schema.examples) ? schema.examples[0] : undefined) ??
  schema.default ??
  schema.enum?.[0];

/** A plausible example value built from the schema's types and examples. */
export const exampleValue = (input: ApiSchema, depth = 0): unknown => {
  const schema = unwrapSchema(input);
  const given = firstExample(schema);
  if (given !== undefined) return given;
  if (schema.recursive || depth > MAX_SCHEMA_DEPTH) return {};
  if (schema.anyOf || schema.oneOf)
    return exampleValue((schema.anyOf ?? schema.oneOf)![0], depth + 1);
  const type = Array.isArray(schema.type)
    ? schema.type.find(item => item !== 'null')
    : schema.type;
  if (type === 'array')
    return schema.items ? [exampleValue(schema.items, depth + 1)] : [];
  if (type === 'object' || schema.properties) {
    if (!schema.properties) return {};
    return Object.fromEntries(
      Object.entries(schema.properties).map(([key, value]) => [
        key,
        exampleValue(value, depth + 1),
      ]),
    );
  }
  if (type === 'integer' || type === 'number') return 0;
  if (type === 'boolean') return true;
  if (schema.format === 'date-time') return '2026-01-01T00:00:00Z';
  if (schema.format === 'date') return '2026-01-01';
  if (schema.format === 'uuid') return '00000000-0000-0000-0000-000000000000';
  return 'string';
};

const operationParameters = (
  spec: any,
  pathItem: any,
  operation: any,
): ApiParameter[] => {
  // Operation parameters override path-level ones with the same name and place.
  const merged = new Map<string, any>();
  for (const raw of [
    ...(pathItem.parameters ?? []),
    ...(operation.parameters ?? []),
  ]) {
    const parameter = raw.$ref ? lookupRef(spec, raw.$ref) : raw;
    if (parameter?.name)
      merged.set(`${parameter.in}:${parameter.name}`, parameter);
  }
  return [...merged.values()].map(parameter => ({
    name: parameter.name,
    in: parameter.in,
    required: !!parameter.required || parameter.in === 'path',
    description: parameter.description,
    schema: resolveSchema(spec, parameter.schema ?? {}),
  }));
};

const jsonSchema = (spec: any, content: any): ApiSchema | undefined => {
  const media =
    content?.['application/json'] ?? Object.values(content ?? {})[0];
  return media && (media as any).schema
    ? resolveSchema(spec, (media as any).schema)
    : undefined;
};

/** Public operations of one service's OpenAPI document, in spec order. */
export const publicOperations = (
  spec: any,
  service: string,
): ApiOperation[] => {
  const operations: ApiOperation[] = [];
  const seen = new Set<string>();
  for (const [path, pathItem] of Object.entries<any>(spec?.paths ?? {})) {
    for (const method of HTTP_METHODS) {
      const operation = pathItem?.[method];
      if (!operation?.tags?.includes(PUBLIC_API_TAG)) continue;
      let id = slug(operation.operationId ?? `${method} ${path}`);
      while (seen.has(id)) id = `${id}-${method}`;
      seen.add(id);
      const requestBody = operation.requestBody?.$ref
        ? lookupRef(spec, operation.requestBody.$ref)
        : operation.requestBody;
      operations.push({
        service,
        id,
        method,
        path,
        summary: operation.summary ?? `${method.toUpperCase()} ${path}`,
        description: operation.description,
        deprecated: !!operation.deprecated,
        parameters: operationParameters(spec, pathItem, operation),
        requestBody: requestBody
          ? {
              required: !!requestBody.required,
              schema: jsonSchema(spec, requestBody.content),
            }
          : undefined,
        responses: Object.entries<any>(operation.responses ?? {}).map(
          ([status, response]): ApiResponse => {
            const resolved = response?.$ref
              ? lookupRef(spec, response.$ref)
              : response;
            return {
              status,
              description: resolved?.description,
              schema: jsonSchema(spec, resolved?.content),
            };
          },
        ),
      });
    }
  }
  return operations;
};

/** Starting values for Try it and the code samples. */
export const initialParameterValues = (
  operation: ApiOperation,
  environmentId?: string | null,
): ApiParameterValues =>
  Object.fromEntries(
    operation.parameters
      .filter(parameter => parameter.in === 'path' || parameter.in === 'query')
      .map(parameter => {
        const schema = unwrapSchema(parameter.schema);
        const given = firstExample(schema);
        const value =
          given !== undefined && given !== null
            ? String(given)
            : environmentId && /^environment_?id$/i.test(parameter.name)
            ? environmentId
            : '';
        return [parameter.name, value];
      }),
  );

/** Path parameters that are missing, for Try it validation. */
export const missingParameters = (
  operation: ApiOperation,
  values: ApiParameterValues,
): string[] =>
  operation.parameters
    .filter(
      parameter =>
        (parameter.in === 'path' || parameter.in === 'query') &&
        parameter.required &&
        !values[parameter.name]?.trim(),
    )
    .map(parameter => parameter.name);

/**
 * The request path and query for an operation. Values are encoded, and a
 * path value of "." or ".." is rejected so it can't climb to another route.
 */
export const operationRequestPath = (
  operation: ApiOperation,
  values: ApiParameterValues,
): string => {
  const path = operation.path.replace(/\{([^}]+)\}/g, (_, name: string) => {
    const value = values[name]?.trim() ?? '';
    if (value === '.' || value === '..')
      throw new Error(`Invalid value for ${name}`);
    return value ? encodeURIComponent(value) : `{${name}}`;
  });
  const query = new URLSearchParams();
  for (const parameter of operation.parameters)
    if (parameter.in === 'query' && values[parameter.name]?.trim())
      query.append(parameter.name, values[parameter.name].trim());
  const search = query.toString();
  return search ? `${path}?${search}` : path;
};

/** Code samples use a placeholder token, never the signed-in user's token. */
export const codeSamples = (
  operation: ApiOperation,
  baseUrl: string,
  values: ApiParameterValues,
): Record<'curl' | 'python' | 'javascript', string> => {
  let requestPath: string;
  try {
    requestPath = operationRequestPath(operation, values);
  } catch {
    requestPath = operation.path;
  }
  const url = `${baseUrl}${requestPath}`;
  const method = operation.method.toUpperCase();
  const body =
    operation.requestBody?.schema &&
    JSON.stringify(exampleValue(operation.requestBody.schema), null, 2);
  const curl = [
    `curl -s${method === 'GET' ? '' : ` -X ${method}`} "${url}"`,
    '  -H "Authorization: Bearer $ARIKSA_TOKEN"',
    ...(body
      ? [
          '  -H "Content-Type: application/json"',
          `  -d '${body.replace(/'/g, "'\\''")}'`,
        ]
      : []),
  ].join(' \\\n');
  const python = [
    'import os',
    'import requests',
    '',
    `response = requests.${operation.method}(`,
    `    "${url}",`,
    '    headers={"Authorization": f"Bearer {os.environ[\'ARIKSA_TOKEN\']}"},',
    ...(body ? [`    json=${body.replace(/\n/g, '\n    ')},`] : []),
    ')',
    'response.raise_for_status()',
    'print(response.json())',
  ].join('\n');
  const javascript = [
    `const response = await fetch("${url}", {`,
    ...(method === 'GET' ? [] : [`  method: "${method}",`]),
    '  headers: {',
    // The sample's own template string, not one evaluated here.
    // eslint-disable-next-line no-template-curly-in-string
    '    Authorization: `Bearer ${process.env.ARIKSA_TOKEN}`,',
    ...(body ? ['    "Content-Type": "application/json",'] : []),
    '  },',
    ...(body
      ? [`  body: JSON.stringify(${body.replace(/\n/g, '\n  ')}),`]
      : []),
    '});',
    'console.log(await response.json());',
  ].join('\n');
  return { curl, python, javascript };
};

/** Pretty JSON for display; other text is returned as is. */
export const formatResponseBody = (text: string): string => {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
};
